import React, { useState } from "react";
import { useUpdateMarketplaceItemMutation } from "../../../../store/slices/api";
import {
  HANDLE_LABELS,
  PAYMENT_PLATFORMS,
  handlePreview,
  isLinkablePlatform,
  normalizeHandle,
  paymentHandleError,
  sellerEmailError,
} from "../payLinks";
import { CATEGORIES, itemCategoryLabel } from "./itemFormat";
import { serverReason } from "../serverReason";

// The same limits as the posting form.
const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 2000;
const MAX_EMAIL_LENGTH = 100;
const MAX_HANDLE_LENGTH = 100;

const SAVE_FAILED = "Your changes couldn't be saved. Nothing was lost — please check the details and try again.";

const fieldClass =
  "w-full rounded-[8px] border border-dupontGold/60 dark:border-darkGold/30 bg-white dark:bg-darkElevated " +
  "px-[12px] py-[9px] text-[15px] font-body text-dupontBlue dark:text-darkText " +
  "focus:outline focus:outline-2 focus:outline-dupontBlue dark:focus:outline-darkGold";
const labelClass = "flex flex-col gap-y-[4px] text-[14px] font-semibold text-dupontBlue dark:text-darkText";
const errorClass = "text-[13px] font-normal text-red-700 dark:text-red-400";

function priceText(price) {
  if (price === null || price === undefined || price === "") return "";
  const n = Number(price);
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : "";
}

/**
 * The seller's own edit of a listing, inside the listing window.
 *
 * Plain <select>s, not the posting page's pop-up menus: those open outside the
 * window, and a click there would count as a click outside and close it.
 * Photos and attachments stay as posted (the server doesn't change them).
 */
function EditItem({ item, onDone }) {
  const [updateItem, { isLoading }] = useUpdateMarketplaceItemMutation();
  const [title, setTitle] = useState(item.title || "");
  const [description, setDescription] = useState(item.description || "");
  const [category, setCategory] = useState(item.category || "");
  const [categoryOther, setCategoryOther] = useState(item.category_other || "");
  const [price, setPrice] = useState(priceText(item.price));
  const [priceUnit, setPriceUnit] = useState(item.price_unit || "");
  const [sellerEmail, setSellerEmail] = useState(item.seller_email || "");
  const [platform, setPlatform] = useState(item.payment_platform || "");
  const [handle, setHandle] = useState(item.payment_handle || "");
  const [submitError, setSubmitError] = useState(null);

  const savedHandle = () => {
    const trimmed = handle.trim();
    if (!platform || !trimmed) return null;
    return isLinkablePlatform(platform) ? normalizeHandle(platform, trimmed) : trimmed;
  };

  // What the form would save, next to what the listing holds now. Only the
  // fields the seller changed are sent and checked, so an older listing whose
  // saved handle predates today's rules can still get a title fix.
  const form = {
    title,
    description,
    category,
    category_other: category === "other" ? categoryOther : null,
    price: price ? parseFloat(price) : null,
    price_unit: priceUnit || null,
    seller_email: sellerEmail.trim(),
    payment_platform: platform || null,
    payment_handle: savedHandle(),
  };
  const saved = {
    title: item.title || "",
    description: item.description || "",
    category: item.category || "",
    category_other: item.category === "other" ? item.category_other || "" : null,
    price: item.price ?? null,
    price_unit: item.price_unit || null,
    seller_email: item.seller_email || "",
    payment_platform: item.payment_platform || null,
    payment_handle: item.payment_handle || null,
  };
  const same = (a, b) => (a ?? null) === (b ?? null);
  // The handle is judged by what the seller typed, not its tidied form, so an
  // untouched older handle never counts as a change.
  const handleTyped = handle.trim() !== (item.payment_handle || "").trim();
  const changed = Object.keys(form).filter((k) =>
    k === "payment_handle" ? handleTyped : !same(form[k], saved[k])
  );
  const paymentChanged = changed.includes("payment_platform") || changed.includes("payment_handle");

  const handleError = platform && paymentChanged ? paymentHandleError(platform, handle) : null;
  const emailError = changed.includes("seller_email") ? sellerEmailError(sellerEmail) : null;
  // "." or "5." alone is not a price; the posting form turned it into "Make an offer".
  const priceError = price !== "" && !Number.isFinite(parseFloat(price)) ? "Enter a price like 25 or 25.50, or leave it blank." : null;
  const canSave =
    !isLoading &&
    !handleError &&
    !emailError &&
    !priceError &&
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    Boolean(category) &&
    sellerEmail.trim().length > 0;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    if (changed.length === 0) {
      onDone();
      return;
    }
    setSubmitError(null);
    const body = { id: item.id };
    for (const k of changed) body[k] = form[k];
    // Moving out of "Other" clears the old Other name on the server too.
    if (changed.includes("category") && category !== "other") body.category_other = null;
    let result;
    try {
      result = await updateItem(body).unwrap();
    } catch (err) {
      const reason = serverReason(err);
      setSubmitError(reason ? `${SAVE_FAILED} (${reason})` : SAVE_FAILED);
      return;
    }
    // A server that answered "OK" without saving (an older version only saved
    // Sold) must not look like success.
    const kept = changed.every((k) =>
      k === "price"
        ? (result?.price == null ? form.price === null : Number(result.price) === form.price)
        : same(result?.[k], form[k])
    );
    if (!kept) {
      setSubmitError("Your changes didn't save. Please try again in a few minutes.");
      return;
    }
    onDone();
  };

  const id = (name) => `edit-item-${item.id}-${name}`;
  const preview = handlePreview(platform, handle);

  return (
    <form
      onSubmit={handleSave}
      aria-label="Edit your listing"
      className="flex flex-col gap-y-[14px] rounded-[12px] border border-dupontGold/60 dark:border-darkGold/30 bg-dupontCream dark:bg-darkElevated p-[14px]"
    >
      <div className="font-display font-semibold text-[17px] text-dupontBlue dark:text-darkText">
        Edit your listing
      </div>

      <label className={labelClass}>
        Title*
        {/* Focus starts here, so the keyboard lands in the form it just opened. */}
        <input className={fieldClass} value={title} maxLength={MAX_TITLE_LENGTH} autoFocus
               onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className={labelClass}>
        Description*
        <textarea className={fieldClass} rows={4} value={description} maxLength={MAX_DESCRIPTION_LENGTH}
                  onChange={(e) => setDescription(e.target.value)} />
      </label>

      <label className={labelClass}>
        Category*
        <select className={fieldClass} value={category} onChange={(e) => setCategory(e.target.value)}>
          {!category && <option value="">Choose a category</option>}
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{itemCategoryLabel({ category: c })}</option>
          ))}
        </select>
      </label>

      {category === "other" && (
        <label className={labelClass}>
          Describe your category
          <input className={fieldClass} value={categoryOther} maxLength={35}
                 onChange={(e) => setCategoryOther(e.target.value)} />
        </label>
      )}

      <div className="flex flex-col sm:flex-row gap-[12px]">
        <label className={labelClass + " sm:flex-1"}>
          {"Price ($) — blank for 'Make an offer'"}
          <input className={fieldClass} inputMode="decimal" value={price}
                 aria-invalid={Boolean(priceError)} aria-describedby={priceError ? id("price-err") : undefined}
                 onChange={(e) => {
                   const val = e.target.value;
                   if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) setPrice(val);
                 }} />
          {priceError && <span id={id("price-err")} className={errorClass}>{priceError}</span>}
        </label>
        <label className={labelClass + " sm:flex-1"}>
          Price unit (like per week)
          <input className={fieldClass} value={priceUnit} maxLength={25}
                 onChange={(e) => setPriceUnit(e.target.value)} />
        </label>
      </div>

      <label className={labelClass}>
        Your email (for buyers to contact you)*
        <input className={fieldClass} type="email" value={sellerEmail} maxLength={MAX_EMAIL_LENGTH}
               aria-invalid={Boolean(emailError)} aria-describedby={emailError ? id("email-err") : undefined}
               onChange={(e) => setSellerEmail(e.target.value)} />
        {emailError && <span id={id("email-err")} className={errorClass}>{emailError}</span>}
      </label>

      <label className={labelClass}>
        How buyers can pay you (optional)
        <select className={fieldClass} value={platform} onChange={(e) => setPlatform(e.target.value)}>
          <option value="">No payment info</option>
          {PAYMENT_PLATFORMS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </label>

      {platform && (
        <label className={labelClass}>
          {HANDLE_LABELS[platform] || "Your handle"}
          <input className={fieldClass} value={handle} maxLength={MAX_HANDLE_LENGTH}
                 aria-invalid={Boolean(handleError)} aria-describedby={handleError ? id("handle-err") : undefined}
                 onChange={(e) => setHandle(e.target.value)} />
          {handleError && <span id={id("handle-err")} className={errorClass}>{handleError}</span>}
          {!handleError && preview && (
            <span className="text-[13px] font-normal text-dupontGray dark:text-darkTextSecondary" aria-live="polite">
              {preview}
            </span>
          )}
        </label>
      )}

      <div className="text-[13px] font-body text-dupontGray dark:text-darkTextSecondary">
        Photos and attachments stay as posted.
      </div>

      {submitError && (
        <div role="alert" className="text-[15px] font-body text-red-700 dark:text-red-400">
          {submitError}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-[10px]">
        <button
          type="button"
          onClick={onDone}
          className="w-full sm:w-auto rounded-[8px] border border-dupontGold px-[16px] py-[10px] text-[15px] font-semibold text-dupontBlue dark:text-darkText hover:bg-dupontGold/10 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="w-full sm:w-auto rounded-[8px] bg-dupontBlue dark:bg-darkGold text-white dark:text-dupontBlue px-[16px] py-[10px] text-[15px] font-semibold hover:opacity-90 disabled:opacity-50 cursor-pointer"
        >
          {isLoading ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
}

export default EditItem;
